import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Paper, TextField, Button, Grid, Alert, CircularProgress, 
  Box, InputAdornment, Autocomplete
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import AssignmentIcon from '@mui/icons-material/Assignment';

const CreateLead = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    subject: '',
    firstname: '',
    lastname: '',
    jobtitle: '',
    companyname: '',
    emailaddress1: '',
    telephone1: '',
    address1_country: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        const response = await axios.get(`${config}/clients`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setClients(response.data);
      } catch (err) {
        console.error('Error fetching clients:', err);
      } finally {
        setLoadingClients(false);
      }
    };
    fetchClients();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (!formData.subject || !formData.lastname) {
      setError('Subject and Last Name are required fields.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${config}/leads`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      navigate(`/leads/${response.data.leadid}`);
    } catch (err) {
      console.error('Error creating lead:', err);
      setError(err.response?.data?.error || 'Failed to create lead.');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>

      {/* Header Section Matches LeadDetails */}
      <Paper elevation={0} sx={{
        p: { xs: 3, md: 5 },
        mb: 4,
          mt: 2,
          borderRadius: 4,
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)'
        }}>
          {/* Background Decorative Elements */}
          <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255, 255, 255, 0.05)' }} />
          <Box sx={{ position: 'absolute', bottom: -30, left: 100, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 3 }}>
              <Box sx={{ flex: 1, width: '100%' }}>
                <Button 
                  startIcon={<ArrowBackIcon />} 
                  onClick={() => navigate('/leads')} 
                  sx={{ color: '#90caf9', mb: 2, pl: 0, fontWeight: 'bold', '&:hover': { color: 'white', bgcolor: 'transparent', transform: 'translateX(-4px)' }, transition: 'all 0.2s' }}
                >
                  Back to Leads
                </Button>
                <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, letterSpacing: '-0.02em', color: 'white' }}>
                  Create New Lead
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#e3f2fd', maxWidth: 600, fontSize: '1.1rem' }}>                  Enter the details and contact information to log a new prospective business opportunity.
                </Typography>
              </Box>            <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button 
                variant="contained"
                disabled={loading}
                onClick={handleSubmit}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                sx={{ 
                  borderRadius: 2, 
                  px: 4, 
                  py: 1.5, 
                  fontSize: '1rem',
                  fontWeight: '800', 
                  boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
                  background: 'linear-gradient(45deg, #3b82f6 0%, #60a5fa 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(59,130,246,0.4)',
                    background: 'linear-gradient(45deg, #2563eb 0%, #3b82f6 100%)'
                  },
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {loading ? 'Creating...' : 'Save Lead'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2, boxShadow: 1 }}>
          {error}
        </Alert>
      )}

      {/* Main Content Area */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5, pb: 2, borderBottom: '1px solid #f1f5f9' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#eff6ff', p: 1.5, borderRadius: 2 }}>
                    <AssignmentIcon sx={{ color: '#3b82f6' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                  Lead Details
                </Typography>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Topic / Subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="e.g., Interested in our new software suite"
                  helperText="Required. A brief description of what this lead is about."
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: '#f8fafc',
                      '&:hover': { bgcolor: '#f1f5f9' },
                      '&.Mui-focused': { bgcolor: 'white', boxShadow: '0 0 0 4px rgba(59,130,246,0.1)' }
                    },
                    '& .MuiInputLabel-root': { fontWeight: 600 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstname"
                  value={formData.firstname}
                  onChange={handleChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PersonIcon color="action" /></InputAdornment>
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: '#f8fafc',
                      '&:hover': { bgcolor: '#f1f5f9' },
                      '&.Mui-focused': { bgcolor: 'white', boxShadow: '0 0 0 4px rgba(59,130,246,0.1)' }
                    },
                    '& .MuiInputLabel-root': { fontWeight: 600 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Last Name"
                  name="lastname"
                  value={formData.lastname}
                  onChange={handleChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PersonIcon color="action" /></InputAdornment>
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: '#f8fafc',
                      '&:hover': { bgcolor: '#f1f5f9' },
                      '&.Mui-focused': { bgcolor: 'white', boxShadow: '0 0 0 4px rgba(59,130,246,0.1)' }
                    },
                    '& .MuiInputLabel-root': { fontWeight: 600 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Job Title"
                  name="jobtitle"
                  value={formData.jobtitle}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: '#f8fafc',
                      '&:hover': { bgcolor: '#f1f5f9' },
                      '&.Mui-focused': { bgcolor: 'white', boxShadow: '0 0 0 4px rgba(59,130,246,0.1)' }
                    },
                    '& .MuiInputLabel-root': { fontWeight: 600 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country/Region"
                  name="address1_country"
                  value={formData.address1_country}
                  onChange={handleChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PublicIcon color="action" /></InputAdornment>
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: '#f8fafc',
                      '&:hover': { bgcolor: '#f1f5f9' },
                      '&.Mui-focused': { bgcolor: 'white', boxShadow: '0 0 0 4px rgba(59,130,246,0.1)' }
                    },
                    '& .MuiInputLabel-root': { fontWeight: 600 }
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5, pb: 2, borderBottom: '1px solid #f1f5f9' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f3ff', p: 1.5, borderRadius: 2 }}>
                    <BusinessIcon sx={{ color: '#8b5cf6' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                  Company Info
                </Typography>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={clients}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.name || '')}
                  loading={loadingClients}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                      setFormData(prev => ({ ...prev, companyname: newValue }));
                    } else if (newValue && newValue.name) {
                      setFormData(prev => ({
                        ...prev,
                        companyname: newValue.name,
                        emailaddress1: newValue.email || prev.emailaddress1,
                        telephone1: newValue.phone || prev.telephone1,
                      }));
                    } else {
                      setFormData(prev => ({ ...prev, companyname: '' }));
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFormData(prev => ({ ...prev, companyname: newInputValue }));
                  }}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        fullWidth
                        label="Company Name (Select or Type New)"
                        name="companyname"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start" sx={{ pl: 1 }}>
                                <BusinessIcon color="action" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {loadingClients ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: '#f8fafc',
                            '&:hover': { bgcolor: '#f1f5f9' },
                            '&.Mui-focused': { bgcolor: 'white', boxShadow: '0 0 0 4px rgba(139,92,246,0.1)' }
                          },
                          '& .MuiInputLabel-root': { fontWeight: 600 }
                        }}
                      />
                    );
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1.5, pb: 2, borderBottom: '1px solid #f1f5f9' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#ecfdf5', p: 1.5, borderRadius: 2 }}>
                        <PhoneIcon sx={{ color: '#10b981' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      Contact Data
                    </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="emailaddress1"
                  type="email"
                  value={formData.emailaddress1}
                  onChange={handleChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><EmailIcon color="action" /></InputAdornment>
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: '#f8fafc',
                      '&:hover': { bgcolor: '#f1f5f9' },
                      '&.Mui-focused': { bgcolor: 'white', boxShadow: '0 0 0 4px rgba(16,185,129,0.1)' }
                    },
                    '& .MuiInputLabel-root': { fontWeight: 600 }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Business Phone"
                  name="telephone1"
                  value={formData.telephone1}
                  onChange={handleChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PhoneIcon color="action" /></InputAdornment>
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: '#f8fafc',
                      '&:hover': { bgcolor: '#f1f5f9' },
                      '&.Mui-focused': { bgcolor: 'white', boxShadow: '0 0 0 4px rgba(16,185,129,0.1)' }
                    },
                    '& .MuiInputLabel-root': { fontWeight: 600 }
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

      </Grid>
    </Container>
  );
};

export default CreateLead;